import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { DbEnums } from '@eluve/graphql-types';

import { useAppointmentMode } from './useAppointmentMode';
import { useAppointmentStatus } from './useAppointmentStatus';

function useSearchParamValue<T extends string>(paramName: string) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const value = params.get(paramName) as T | null;
  const setValue = (value?: T) => {
    if (value) {
      params.set(paramName, value);
    } else {
      params.delete(paramName);
    }

    navigate(`?${params.toString()}`, { replace: true });
  };

  return [value, setValue] as const;
}

export const TRANSCRIPT_TAB = 'transcript' as const;
export const SUMMARY_TAB = 'summary' as const;
export const INSIGHTS_TAB = 'insights' as const;
export const CARE_PLAN_TAB = 'care-plan' as const;

const DOCUMENT_TAB_PREFIX = 'doc-';

export const toDocumentTabId = (documentId: string) =>
  `${DOCUMENT_TAB_PREFIX}${documentId}`;

export const isDocumentTab = (tab: string) =>
  tab.startsWith(DOCUMENT_TAB_PREFIX);

export const parseDocumentTabId = (tab: string) =>
  isDocumentTab(tab) ? tab.slice(DOCUMENT_TAB_PREFIX.length) : null;

const AppointmentTabSchema = z
  .string()
  .nullable()
  .transform((val) => {
    if (!val) return TRANSCRIPT_TAB;
    if (
      [TRANSCRIPT_TAB, SUMMARY_TAB, INSIGHTS_TAB, CARE_PLAN_TAB].includes(
        val as never,
      )
    )
      return val;
    if (isDocumentTab(val)) return val;
    return TRANSCRIPT_TAB;
  });

export type AppointmentTab = string;

/**
 * Read or set the current tab in a type safe way
 */
export const useAppointmentTabNav = () => {
  const [currentTab, setCurrentTab] = useSearchParamValue('tab');
  const appointmentStatus = useAppointmentStatus();
  const appointmentMode = useAppointmentMode();

  const tab =
    !currentTab &&
    (appointmentStatus === 'COMPLETED' ||
      appointmentMode === DbEnums.AppointmentMode.MANUAL_CHARTING)
      ? SUMMARY_TAB
      : AppointmentTabSchema.parse(currentTab);

  const setTab = useCallback(
    (newTab: AppointmentTab) => {
      setCurrentTab(newTab);
    },
    [setCurrentTab],
  );

  // If a tab wasn't explicitly set, update the URL to reflect the current desired
  // state
  useEffect(() => {
    if (!currentTab) {
      setTab(tab);
    }
  }, [tab, currentTab, setTab]);

  return {
    tab,
    setTab,
  };
};
